import { SPLIT_THRESHOLDS } from '../../../shared/constants.js';

export interface EvaluatedStudentBlock {
  block_uid?: string;
  student_id: number;
  pages: number[];
  student_name: string | null;
  final_confidence: number;
  block_issues: string[];
  block_issue_codes?: string[];
}

export interface SplitConfidenceStats {
  pageCounts: number[];
  medianPages: number;
  stdDeviation: number;
}

export function calculateSplitConfidenceStats(rawBlocks: any[]): SplitConfidenceStats {
  const pageCounts = rawBlocks.map(b => (Array.isArray(b.pages) ? b.pages.length : 0));
  const sortedCounts = [...pageCounts].sort((a, b) => a - b);
  const mid = Math.floor(sortedCounts.length / 2);
  const medianPages =
    sortedCounts.length % 2 === 1 ? sortedCounts[mid] : (sortedCounts[mid - 1] + sortedCounts[mid]) / 2;
  const variance = pageCounts.reduce((sum, count) => sum + (count - medianPages) ** 2, 0) / pageCounts.length;
  const stdDeviation = Math.sqrt(variance);

  return { pageCounts, medianPages, stdDeviation };
}

/**
 * Calculates a confidence score (0.0 to 1.0) and generates warnings for each student block
 * based on LLM evidence, missing data, and statistical deviation of page counts.
 */
export function evaluateSplitConfidence(rawBlocks: any[], stats?: SplitConfidenceStats): EvaluatedStudentBlock[] {
  if (!rawBlocks || rawBlocks.length === 0) return [];
  const hasMinimumGroupSize = rawBlocks.length >= 5;

  // --- PHASE 1: Global Stats (Mean & Standard Deviation) ---
  const resolvedStats = stats ?? calculateSplitConfidenceStats(rawBlocks);
  const { medianPages, stdDeviation } = resolvedStats;

  // --- PHASE 2: Evaluate Each Block ---
  return rawBlocks.map((rawBlock, index, allBlocks) => {
    // Normalize basic fields
    const student_id = index + 1;
    const block_uid =
      typeof rawBlock?.block_uid === 'string' && rawBlock.block_uid.trim().length > 0
        ? rawBlock.block_uid.trim()
        : `local-block-${student_id}`;
    const pages = Array.isArray(rawBlock.pages) ? rawBlock.pages.map(Number).sort((a, b) => a - b) : [];
    const student_name =
      typeof rawBlock.student_name === 'string' && rawBlock.student_name.trim().length > 0
        ? rawBlock.student_name
        : null;

    let confidence = typeof rawBlock.block_confidence === 'number' ? rawBlock.block_confidence : 1.0;
    const issues: string[] = [];
    const issueCodes: string[] = [];

    const hasStartMarkerValue = rawBlock.has_start_marker;
    const hasStartMarkerProvided = typeof hasStartMarkerValue === 'boolean';
    const hasStartMarker = hasStartMarkerValue === true;
    const nameExtractionConfidenceProvided = typeof rawBlock.name_extraction_confidence === 'number';
    const nameExtractionConfidence = nameExtractionConfidenceProvided ? rawBlock.name_extraction_confidence : undefined;
    // 1. Missing Start Boundary
    if (hasStartMarkerProvided && !hasStartMarker) {
      confidence -= 0.15;
      // issues.push('Erste Seite sieht optisch nicht nach einem Klausurbeginn aus.');
      // issueCodes.push('missing_start_boundary');
    }

    // 2. Missing or Uncertain Identity
    if (!student_name) {
      confidence -= 0.2;
      issues.push('Kein eindeutiger Schülername gefunden.');
      issueCodes.push('missing_student_name');
    } else if (
      nameExtractionConfidenceProvided &&
      (nameExtractionConfidence as number) < SPLIT_THRESHOLDS.LOW_CONFIDENCE
    ) {
      confidence -= 0.1;
      issues.push('Gefundener Schülername ist unsicher oder schwer lesbar.');
      issueCodes.push('name_uncertain');
    }

    // 3. Statistical Deviation (2x std-dev = warning, 3x std-dev = extreme)
    const pageCount = pages.length;
    const outlierDistance = 2 * stdDeviation;
    const extremeOutlierDistance = 3 * stdDeviation;
    const isLongExtremeOutlier = stdDeviation > 0 && pageCount >= medianPages + extremeOutlierDistance;
    const isShortExtremeOutlier = stdDeviation > 0 && pageCount <= medianPages - extremeOutlierDistance;
    const isLongOutlier =
      stdDeviation > 0 &&
      pageCount >= medianPages + outlierDistance &&
      pageCount < medianPages + extremeOutlierDistance;
    const isShortOutlier =
      stdDeviation > 0 &&
      pageCount <= medianPages - outlierDistance &&
      pageCount > medianPages - extremeOutlierDistance;

    if (hasMinimumGroupSize && (isLongExtremeOutlier || isShortExtremeOutlier || isLongOutlier || isShortOutlier)) {
      confidence -= 0.15;
      // const formattedMedian = Number(medianPages).toFixed(1);
      // const formattedStdDev = stdDeviation.toFixed(1);
      issues.push(
        `Seitenanzahl (${pageCount}) weicht stark vom Durchschnitt ab.`, // (${formattedMedian}) ab (Standardabweichung: ${formattedStdDev}).
      );
      if (isLongExtremeOutlier) {
        issueCodes.push('outlier_long_extreme');
      } else if (isShortExtremeOutlier) {
        issueCodes.push('outlier_short_extreme');
      } else if (isLongOutlier) {
        issueCodes.push('outlier_long');
      } else {
        issueCodes.push('outlier_short');
      }
    }

    // 4. Page continuity check within a student block
    const hasNonConsecutivePages = pages.some((page, pageIndex) => pageIndex > 0 && page - pages[pageIndex - 1]! > 1);
    if (hasNonConsecutivePages) {
      confidence -= 0.1;
      issues.push('Seiten innerhalb einer Abgabe sind nicht aufeinanderfolgend.');
      issueCodes.push('non_consecutive_pages');
    }

    // 5. Transition Check (Look ahead to next block)
    const nextBlock = allBlocks[index + 1];
    if (nextBlock && nextBlock.has_start_marker !== true) {
      confidence -= 0.1;
      // issues.push(
      //   'Der Übergang zum nächsten Schüler ist unklar (folgender Abschnitt hat keine eindeutige Startseite).',
      // );
      // issueCodes.push('transition_uncertain');
    }

    // Ensure final score stays within bounds
    confidence = Math.max(0.0, Math.min(1.0, confidence));
    return {
      block_uid,
      student_id,
      pages,
      student_name,
      final_confidence: Number(confidence.toFixed(2)),
      block_issues: issues,
      block_issue_codes: issueCodes,
    };
  });
}
